import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import apiFetch from '../../../api/apiFetch'
import EditTargetV2 from './EditTargetV2'
import styles from '../css/ChangeTargetV2.module.css'
import { isDIR, DIR_REASON, DIR_SUB_REASONS, isMOT, MOT_REASON, TYPE_ORDER } from '../targetingUtils'
import Icon from '../../../components/Icon/Icon';

export default function ChangeTargetV2({ row, exercise, user, terr, lock, getTotals, options, onRowUpdate, allowDIR, allowMOT }) {
    const [showModal, setShowModal]     = useState(false)
    const [dirMode, setDirMode]         = useState(false)
    const [motMode, setMotMode]         = useState(false)
    const [dirSubMode, setDirSubMode]   = useState(false)
    const [pendingType, setPendingType] = useState(null)
    const [targetType, setTargetType]   = useState(row.TARGET_TYPE || '')
    const [reason, setReason]           = useState(row.REASON || '')
    const [notes, setNotes]             = useState(row.NOTES  || '')
    const [showTypePicker, setShowTypePicker] = useState(false)
    const [pickerRect, setPickerRect]         = useState(null)
    const actionsRef = useRef(null)

    // Sync local state when the row is externally reset (e.g. "Undo session changes")
    useEffect(() => {
        setTargetType(row.TARGET_TYPE || '')
        setReason(row.REASON || '')
        setNotes(row.NOTES  || '')
    }, [row.TARGET_TYPE, row.REASON, row.NOTES])

    const proposedType   = row.CURRENT_TARGET_VALUE
    // effectiveFinal mirrors deriveRowStatus: TARGET_TYPE override → CHANGED_TARGET_VALUE → fallback to proposed
    const effectiveFinal = targetType || row.CHANGED_TARGET_VALUE || proposedType
    const isMOTActive = reason === MOT_REASON
    const isDropped   = !!(proposedType && proposedType !== 'NT' && (!effectiveFinal || effectiveFinal === 'NT'))
    // hasOverride excludes MOT — MOT preserves the target type so it looks like no change
    const hasOverride = !isMOTActive && targetType !== '' && targetType !== 'NT' && targetType !== proposedType

    const perTargetLimits = exercise?.PER_TARGET_LIMITS ? JSON.parse(exercise.PER_TARGET_LIMITS) : null
    const limitForType    = perTargetLimits?.[proposedType]
    const allowChanges    = limitForType?.allowChanges !== false
    const permittedTypes  = limitForType?.permittedTargetChanges ?? ['A', 'B', 'C', 'D', 'E'].filter(t => t !== proposedType)

    // NT/null proposedType → any valid pending type is an upgrade; NT pending type is always a drop
    const isUpgrade  = pendingType && pendingType !== 'NT'
        ? (!proposedType || proposedType === 'NT' || TYPE_ORDER.indexOf(pendingType) < TYPE_ORDER.indexOf(proposedType))
        : false
    const optionCodes = Array.isArray(options)
        ? options
        : (isUpgrade ? (options?.upgrade ?? []) : (options?.downgrade ?? []))
    const needsReason = optionCodes.length > 0

    const callApi = (target, reasons, isExisting) => {
        const payload = {
            ...reasons,
            ID: exercise.TARGETING_EXERCISE_ID,
            terr,
            type: exercise.CUSTOMER_TYPE,
            mdmID: row.PROF_ID,
            target: target !== '' ? target : proposedType,
            oldTarget: targetType !== '' ? targetType : proposedType,
            user,
            role: user.role,
            username: user.userName,
        }
        const endpoint = '/api/v2/targeting/set-target-type'

        apiFetch(endpoint, payload).then(() => {
            setTargetType(target)
            setReason(reasons?.reason || '')
            setNotes(reasons?.notes  || '')
            setShowModal(false)
            setShowTypePicker(false)
            if (getTotals) getTotals()
            if (onRowUpdate) onRowUpdate({
                ...row,
                TARGET_TYPE: target,
                // Keep CHANGED_TARGET_VALUE in sync so the Q2 column reflects reality immediately;
                // when target is '' (undo), clear it so Q2 falls back to CURRENT_TARGET_VALUE
                ...(target !== '' ? { CHANGED_TARGET_VALUE: target } : { CHANGED_TARGET_VALUE: undefined }),
                REASON: reasons?.reason || '',
                NOTES: reasons?.notes  || '',
            })
        })
    }

    const handleTypeSelect = (type) => {
        if (needsReason) {
            setPendingType(type)
            setShowModal(true)
            setShowTypePicker(false)
        } else {
            const isExisting = !!(row.TARGET_TYPE || targetType)
            callApi(type, {}, isExisting)
        }
    }

    const handleModalSubmit = (type, reasons) => {
        const isExisting = !!(row.TARGET_TYPE || targetType)
        callApi(type, reasons, isExisting)
    }

    const handleDrop = () => {
        callApi('', {}, true)
    }

    const handleDIR = () => {
        // Use 'NT' (not '') so callApi doesn't fall back to currentQ1 in the payload.
        // Backend treats NT as a drop (isRealTarget=false) but hasReason=true so the
        // change record is persisted with TARGET_TYPE='NT', REASON='Deceased/Inactive/Retired'.
        callApi('NT', { reason: DIR_REASON, notes: '' }, true)
    }

    const handleMOT = () => {
        // Preserve the current effective final — target type does NOT change.
        // Record REASON='Moved Out of Territory' so the backend excludes it from limits.
        const effectiveTarget = targetType || row.CHANGED_TARGET_VALUE || proposedType
        callApi(effectiveTarget || proposedType, { reason: MOT_REASON, notes: '' }, !!(row.TARGET_TYPE))
    }

    const handleRestore = () => {
        callApi(proposedType, {}, false)
    }

    const handleEdit = () => {
        setPendingType(targetType)
        setDirMode(false)
        setShowModal(true)
    }

    const handleEditDIR = () => {
        // Open the modal in read-only DIR mode so the rep can see why and undo
        setDirMode(true)
        setShowModal(true)
    }

    const handleEditMOT = () => {
        // Open the modal in read-only MOT mode so the rep can see it and undo
        setMotMode(true)
        setShowModal(true)
    }

    const handleShowPicker = () => {
        if (actionsRef.current) {
            const r = actionsRef.current.getBoundingClientRect()
            const PICKER_WIDTH = 160
            const PICKER_HEIGHT = 200

            // Horizontal: prefer left-aligned, flip right if near right edge
            const spaceRight = window.innerWidth - r.left
            const hAnchor = spaceRight < PICKER_WIDTH + 8
                ? { right: window.innerWidth - r.right }
                : { left: r.left }

            // Vertical: prefer below, flip above if near bottom edge
            const spaceBelow = window.innerHeight - r.bottom
            const vAnchor = spaceBelow < PICKER_HEIGHT + 8
                ? { bottom: window.innerHeight - r.top + 6 }
                : { top: r.bottom + 6 }

            setPickerRect({ ...hAnchor, ...vAnchor })
        }
        setShowTypePicker(true)
    }

    return (
        <div className={styles.wrap}>
            {/* EditTargetV2 modal — portalled to body to escape table overflow/stacking context */}
            {showModal && createPortal(
                <EditTargetV2
                    row={row}
                    targetType={pendingType || targetType}
                    exercise={exercise}
                    options={options}
                    reason={reason}
                    notes={notes}
                    lock={lock}
                    dirMode={dirMode}
                    motMode={motMode}
                    onSubmit={handleModalSubmit}
                    onUndo={() => { handleRestore(); setShowModal(false); setDirMode(false) }}
                    onUndoMOT={() => { handleDrop(); setShowModal(false); setMotMode(false) }}
                    onClose={() => { setShowModal(false); setPendingType(null); setDirMode(false); setMotMode(false) }}
                />,
                document.body
            )}

            {/* Type picker — portalled to body, positioned via getBoundingClientRect */}
            {showTypePicker && pickerRect && createPortal(
                <>
                    <div
                        className={styles.pickerBackdrop}
                        onClick={() => { setShowTypePicker(false); setDirSubMode(false) }}
                    />
                    <div
                        className={styles.picker}
                        style={{
                            position: 'fixed',
                            ...(pickerRect.bottom !== undefined ? { bottom: pickerRect.bottom } : { top: pickerRect.top }),
                            ...(pickerRect.right  !== undefined ? { right:  pickerRect.right  } : { left:  pickerRect.left  }),
                        }}
                    >
                        <div className={styles.pickerLabel}>Select Final type</div>
                        <div className={styles.pickerTypes}>
                            {!dirSubMode && permittedTypes.map(t => (
                                <button
                                    key={t}
                                    className={[styles.typeBtn, styles[`typeBtn${t}`]].join(' ')}
                                    onClick={() => handleTypeSelect(t)}
                                >
                                    {t}
                                </button>
                            ))}
                            {!dirSubMode && allowDIR && proposedType && proposedType !== 'NT' && (
                                <button
                                    className={[styles.typeBtn, styles.typeBtnDIR].join(' ')}
                                    onClick={() => setDirSubMode(true)}
                                    title="Mark as Deceased, Inactive, or Retired — sets to NT, does not count against change limit"
                                >
                                    DIR
                                </button>
                            )}
                            {!dirSubMode && allowMOT && proposedType && proposedType !== 'NT' && (
                                <button
                                    className={[styles.typeBtn, styles.typeBtnMOT].join(' ')}
                                    onClick={() => { setShowTypePicker(false); handleMOT() }}
                                    title="Mark as Moved Out of Territory — target type is preserved, does not count against change limit"
                                >
                                    MOT
                                </button>
                            )}
                            {dirSubMode && (
                                <>
                                    <button
                                        className={styles.dirBackBtn}
                                        onClick={() => setDirSubMode(false)}
                                    >
                                        ← Back
                                    </button>
                                    {DIR_SUB_REASONS.map(sub => (
                                        <button
                                            key={sub}
                                            className={[styles.typeBtn, styles.typeBtnDIR].join(' ')}
                                            onClick={() => {
                                                callApi('NT', { reason: sub, notes: '' }, true)
                                                setShowTypePicker(false)
                                                setDirSubMode(false)
                                            }}
                                        >
                                            {sub}
                                        </button>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}

            {/* Current state display */}
            <div className={styles.actions} ref={actionsRef}>
                {hasOverride && (
                    <>
                        <span className={[styles.chip, styles[`chip${targetType}`]].join(' ')}>{targetType}</span>
                        <div className={styles.actionBtns}>
                            <button className={styles.editBtn} onClick={handleEdit} disabled={lock} title="Edit type or reason">
                                <Icon name="edit" />
                            </button>
                            <button className={styles.dropBtn} onClick={handleDrop} disabled={lock} title="Undo this type change">
                                Undo
                            </button>
                        </div>
                    </>
                )}

                {!hasOverride && !isDropped && (
                    <>
                        {isMOTActive
                            ? (
                                <>
                                    <span className={styles.motLabel}>MOT</span>
                                    <button
                                        className={styles.editBtn}
                                        onClick={handleEditMOT}
                                        disabled={lock}
                                        title="View MOT details / undo"
                                    >
                                        <Icon name="edit" />
                                    </button>
                                </>
                            ) : isDIR(row)
                            ? (
                                // Newly-added row that was DIR'd — add was undone, row is neutral
                                // Show DIR badge + pencil so rep knows it's been marked DIR
                                <>
                                    <span className={styles.dirLabel}>DIR</span>
                                    <button
                                        className={styles.editBtn}
                                        onClick={handleEditDIR}
                                        disabled={lock}
                                        title="View DIR details / undo"
                                    >
                                        <Icon name="edit" />
                                    </button>
                                </>
                            ) : (
                                <button
                                    className={styles.addBtn}
                                    onClick={handleShowPicker}
                                    disabled={lock || !allowChanges}
                                    title="Add or change Final type"
                                >
                                    {proposedType ? 'Edit' : 'Add'}
                                </button>
                            )
                        }
                    </>
                )}

                {isDropped && (
                    <>
                        {isDIR(row) && <span className={styles.dirLabel}>DIR</span>}
                        <button
                            className={isDIR(row) ? styles.editBtn : styles.restoreBtn}
                            onClick={isDIR(row) ? handleEditDIR : handleRestore}
                            disabled={lock}
                            title={isDIR(row) ? 'View DIR details / undo' : 'Restore original target'}
                        >
                            {isDIR(row) ? <Icon name="edit" /> : 'Restore'}
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}
